/* eslint-disable react/display-name */
import React from 'react';

import { useQuery } from '@apollo/client';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Markdown from 'components/@common/Markdown';

import CircularProgress from 'components/@common/CircularProgress';
import GET_SUPPORT_TOPIC from 'graph/support/support-topic-query';
/*
TODO: Uncomment when adding related topics
import getIcon from '../../utils/get-icon';
import SupportTile from '../../components/SupportTile';
import SupportTileGrid from '../../components/SupportTileGrid';
*/
import { Tag } from 'types/schema-generated-types';
import { parseActionRouteParams } from 'hooks/route-action/util';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5),
      paddingLeft: theme.spacing(5)
    },
    body: {
      overflow: 'auto'
    },
    detailHeader: {
      marginTop: theme.spacing(4)
    },
    description: {
      color: theme.palette.grey[700],
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5)
    },
    closeButton: {
      margintTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
      marginRight: theme.spacing(4)
    },
    tag: {
      textTransform: 'lowercase',
      fontSize: theme.typography.pxToRem(12),
      height: `${theme.spacing(3)}px !important`,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.common.white,
      marginLeft: theme.spacing(1),
      boxShadow: 'none'
    },
    tagLabel: {
      paddingRight: theme.spacing(0.5)
    },
    innerBody: {
      marginTop: theme.spacing(3.5)
    },
    relatedTopics: {
      paddingTop: theme.spacing(4),

      paddingBottom: theme.typography.pxToRem(127)
    },
    tileGrid: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5)
    },
    footer: {
      position: 'absolute',
      width: '100%',
      bottom: 0,
      backgroundColor: theme.palette.common.white
    },
    footerInnerContainer: {
      width: '95%',
      justifyContent: 'flex-end',
      alignItems: 'center',
      display: 'flex',
      paddingTop: theme.spacing(2.5)
    }
  };
});

export interface TopicDetailsProps {
  onClose: () => void;
}

const TopicDetailsModal: React.FC<TopicDetailsProps> = ({ onClose }) => {
  const history = useHistory();
  const { supportTopicId } = parseActionRouteParams(history.location.search);
  const classes = useStyles();
  const { data, loading } = useQuery(GET_SUPPORT_TOPIC, {
    variables: { where: { id: +supportTopicId } }
  });

  const { t } = useTranslation('TopicDetails');
  return (
    <div className={classes.root}>
      <CircularProgress loading={loading} />
      <div className={classes.body}>
        <div className={classes.header}>
          <Typography align="left" variant="h2" component="h1">
            {data?.supportTopic?.supportCategories?.length
              ? data.supportTopic.supportCategories[0].title
              : 'Help and Support'}
            {/* failsafe */}
          </Typography>
        </div>
        <Divider />
        <Container>
          <div className={classes.detailHeader}>
            <Typography align="left" variant="h2" component="h1">
              {t(data?.supportTopic?.title)}
            </Typography>
            <Typography variant="subtitle1" className={classes.description}>
              {t(data?.supportTopic?.description)}
            </Typography>
            {data?.supportTopic?.tags?.length > 0 && (
              <div>
                <Typography variant="caption" className={classes.tagLabel}>
                  {t('Tags')}:
                </Typography>
                {data?.supportTopic?.tags.map(({ value = '', tagGroup = '' }: Partial<Tag>) => (
                  <Chip
                    className={classes.tag}
                    size="small"
                    key={`${value}-${tagGroup}`}
                    label={value}
                  />
                ))}
              </div>
            )}
          </div>
          <div className={classes.innerBody}>
            <Divider />
            <Markdown source={data?.supportTopic?.content} />
          </div>

          {/*
          TODO: This is where related topics will be handled but for now
          we're removing.
          <Divider />
          <div className={classes.relatedTopics}>
            <Typography align="left" variant="h2" component="h1">
              {t('Related Topics')}
            </Typography>
            <SupportTileGrid spacing={4} className={classes.tileGrid}>
              <SupportTile
                title={'Test Related Topic 1'}
                CategoryHeaderProps={{
                  label: 'Getting Started',
                  icon: getIcon('Getting Started'),
                  card: true,
                  size: 'small'
                }}
              >
                This is a test descriptions
              </SupportTile>
              <SupportTile
                title={'Test Related Topic 2'}
                CategoryHeaderProps={{
                  label: 'Getting Started',
                  icon: getIcon('Getting Started'),
                  card: true,
                  size: 'small'
                }}
              >
                This is a test descriptions
              </SupportTile>
              <SupportTile
                title={'Test Related Topic 3'}
                CategoryHeaderProps={{
                  label: 'Getting Started',
                  icon: getIcon('Getting Started'),
                  card: true,
                  size: 'small'
                }}
              >
                This is a test descriptions
              </SupportTile>
            </SupportTileGrid>
          </div>
        */}
          {/* TODO: Remove this div when related topics get added*/}
          <div className={classes.relatedTopics} />
        </Container>
        <div className={classes.footer}>
          <Divider />
          <div className={classes.footerInnerContainer}>
            <Button
              aria-label={t('Close')}
              variant="contained"
              color="secondary"
              onClick={onClose}
              className={classes.closeButton}
              disableElevation
            >
              {t('Close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicDetailsModal;
